import { forwardRef, type ChangeEvent, type CSSProperties, type ReactNode } from 'react';

/**
 * RwToggle — the DS Toggle (rw-ds.css `.rw-toggle`, rw-ds-components § Toggle).
 *
 * A reversible binary that applies immediately (no Save gate) — a view-scoping
 * toggle, a preference, or a display-layer switch. Not for a Save-gated change
 * (→ Checkbox) or a one-of-N view switch (→ Content switcher).
 */
export interface RwToggleProps {
  checked: boolean;
  onChange: (checked: boolean, event: ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  size?: 'md' | 'sm';
  /** Label beside the track — only for dense multi-toggle settings lists. Default: label above. */
  labelPosition?: 'above' | 'left';
  label?: ReactNode;
  /** An adjective ≤3 words ("On" / "Off"), never a verb. */
  actionText?: ReactNode;
  name?: string;
  className?: string;
  'aria-label'?: string;
  /** Forwarded to the underlying `<input>` — lets a caller pair a visible label pattern or target the control directly. */
  id?: string;
  /** Forwarded to the underlying `<input>` when the visible label sits outside this control. */
  'aria-labelledby'?: string;
  /** Forwarded to the underlying `<input>` when helper text sits outside this control. */
  'aria-describedby'?: string;
  'data-testid'?: string;
  /** Forwarded to the underlying `<input>` — e.g. `{ pointerEvents: 'none' }` when disabled, so a
   * `Tooltip` wrapping this control in a `<span>` still receives the hover (MUI's own documented
   * pattern for a disabled native control: https://v6.mui.com/material-ui/react-tooltip/#disabled-elements). */
  style?: CSSProperties;
}

const RwToggle = forwardRef<HTMLInputElement, RwToggleProps>(function RwToggle(
  { checked, onChange, disabled = false, size = 'md', labelPosition = 'above', label, actionText, name, className, ...rest },
  ref
) {
  const classes = [
    'rw-toggle',
    size === 'sm' ? 'rw-toggle--sm' : '',
    labelPosition === 'left' ? 'rw-toggle--label-left' : '',
    className ?? ''
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <label className={classes}>
      {label != null && <span className="rw-toggle__label">{label}</span>}
      <span className="rw-toggle__row">
        <input
          ref={ref}
          type="checkbox"
          role="switch"
          className="rw-toggle__input"
          checked={checked}
          disabled={disabled}
          name={name}
          onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.checked, e)}
          {...rest}
        />
        <span className="rw-toggle__track">
          <span className="rw-toggle__thumb" />
        </span>
        {actionText != null && <span className="rw-toggle__action">{actionText}</span>}
      </span>
    </label>
  );
});

export default RwToggle;
